import React, { useEffect, useRef, useState } from 'react';
import BRAND from '../brandConfig';
import { useApp } from '../context/AppContext';
import { useI18n } from '../i18n';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Seal from '../components/Seal';
import StandardIcon, { IconShield } from '../components/StandardIcons';

// ═══════════════════════════════════════════════════════════════════════════
//  THE PUBLIC HOMEPAGE
//
//  A marketing page, not a portal screen: a full-bleed banner with the offer
//  and one call to action, then the QPS Standard, what we do, finished work,
//  the five-step process, and a closing ask. Layout only — every word and
//  every photo comes from `BRAND.site` in brandConfig.js, so the page is
//  re-worded there and never here.
//
//  Three things carried over from the old homepage that must not be lost,
//  because nothing else on the public site provides them:
//    · the portal door (top right) — the ONLY way a customer or technician
//      reaches their sign-in,
//    · Privacy Policy and Terms in the footer — carriers open these by hand
//      while reviewing our A2P text-messaging registration,
//    · the three dots at the very bottom — Julian's way into the admin login.
//
//  Language: the portals stay fully translated. The marketing prose below is
//  English (it falls back through t() to the brandConfig copy), while the
//  buttons and footer reuse the strings that are already translated, since
//  they do the same job here as they did before. Dropping `lp.*` keys into
//  i18n.js translates the prose too, with no change to this file.
// ═══════════════════════════════════════════════════════════════════════════

export default function Home() {
  const { currentScreen, currentUser, goTo } = useApp();
  const { t } = useI18n();
  const S = BRAND.site;

  // The top bar rides transparent over the banner photo and turns solid once
  // you've scrolled past it. Without this it stays invisible over the cream
  // sections below, where white text on cream is unreadable.
  //
  // Which thing actually scrolls depends on the viewport: `.screen` is a
  // min-height flex column, so on a tall page the DOCUMENT scrolls, while a
  // short one scrolls inside `.lp`. Both are watched rather than guessed at —
  // the bar staying transparent over cream is the one failure that makes the
  // page unusable.
  const [scrolled, setScrolled] = useState(false);
  const scroller = useRef(null);
  const refs = { services: useRef(null), work: useRef(null), about: useRef(null), process: useRef(null) };

  const readScroll = () => setScrolled(
    (window.scrollY || document.documentElement.scrollTop || 0) > 40 ||
    (scroller.current ? scroller.current.scrollTop > 40 : false)
  );

  useEffect(() => {
    if (currentScreen !== 'screen-home') return undefined;
    readScroll();
    window.addEventListener('scroll', readScroll, { passive: true });
    return () => window.removeEventListener('scroll', readScroll);
  }, [currentScreen]); // eslint-disable-line react-hooks/exhaustive-deps

  const toTop = (smooth) => {
    const behavior = smooth ? 'smooth' : 'auto';
    scroller.current?.scrollTo({ top: 0, behavior });
    window.scrollTo({ top: 0, behavior });
  };

  const jumpTo = (key) => {
    const el = refs[key]?.current;
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Leaving a long page has to leave the scroll behind with it: every other
  // screen is a sibling that's merely hidden, so a request form opened from
  // halfway down the homepage would otherwise open halfway down itself.
  const leaveFor = (screen) => { toTop(false); goTo(screen); };

  const request = () => leaveFor('screen-request');

  // Each image slot is `{ src, alt }`. A slot with no photo yet draws its
  // designed panel instead of a broken image — see `site.images` in
  // brandConfig.js.
  //
  // These paint as CSS backgrounds rather than <img>, because every one of them
  // has to cover a panel of unknown shape. That costs the alt text an <img> gets
  // for free, so it is put back by hand: a filled slot is a labelled role="img",
  // an empty one is decoration and stays hidden. Without this the six biggest
  // things on the page are silent to a screen reader.
  const media = (img) => (img?.src ? { backgroundImage: `url(${img.src})` } : undefined);
  const mediaClass = (img, base) => `${base} ${img?.src ? '' : 'empty'}`.trim();
  const mediaLabel = (img) => (img?.src && img.alt
    ? { role: 'img', 'aria-label': img.alt }
    : { 'aria-hidden': 'true' });

  const projects = (S.images.projects || []).slice(0, 4);

  return (
    <div className={`screen ${currentScreen === 'screen-home' ? 'active' : ''}`}>
      <div className="lp" ref={scroller} onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 40)}>

        {/* ── Top bar ─────────────────────────────────────────────────── */}
        <nav className={`lp-nav ${scrolled ? 'solid' : ''}`}>
          <button type="button" className="lp-word" onClick={() => toTop(true)} aria-label={BRAND.name.full}>
            {/* The seal IS the mark — the same drawing the portals and the
                printed work orders use, so one logo runs through everything.
                It is aria-hidden inside Seal; the button's own label is what
                a screen reader reads. */}
            <span className="lp-word-seal"><Seal size={44} /></span>
            <span className="lp-word-rule" />
            <span className="lp-word-lines">{S.wordmark.line1}<br />{S.wordmark.line2}</span>
          </button>

          <div className="lp-links">
            <button type="button" className="lp-link" onClick={() => jumpTo('services')}>{t('lp.nav.services', S.nav.services)}</button>
            <button type="button" className="lp-link" onClick={() => jumpTo('work')}>{t('lp.nav.work', S.nav.work)}</button>
            <button type="button" className="lp-link" onClick={() => jumpTo('about')}>{t('lp.nav.about', S.nav.about)}</button>
            {/* Only shown once there's a Google profile to send people to —
                a nav link that goes nowhere is worse than one less link. */}
            {S.reviewsUrl ? (
              <a className="lp-link" href={S.reviewsUrl} target="_blank" rel="noopener noreferrer">{t('lp.nav.reviews', S.nav.reviews)}</a>
            ) : null}
          </div>

          <div className="lp-navactions">
            {currentUser && (
              <button type="button" className="lp-link" onClick={() => leaveFor(
                currentUser.role === 'admin' ? 'screen-admin-dashboard' :
                currentUser.role === 'vendor' ? 'screen-vendor-dashboard' :
                'screen-homeowner-dashboard'
              )}>{t('nav.dashboard')}</button>
            )}
            <button type="button" className="lp-navcta" onClick={request}>{t('hero.cta', S.nav.cta)}</button>
            <LanguageSwitcher dark />
            {/* Portal door → the chooser (client portal / technician portal). */}
            <button type="button" className="lp-navicon" aria-label="Portals" onClick={() => leaveFor('screen-login-choice')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="4" y="4" width="16" height="7" rx="2.5" />
                <line x1="4" y1="15.5" x2="16" y2="15.5" />
                <line x1="4" y1="19.5" x2="12" y2="19.5" />
              </svg>
            </button>
          </div>
        </nav>

        {/* ── Banner ──────────────────────────────────────────────────── */}
        <header className="lp-banner">
          <div className={mediaClass(S.images.banner, 'lp-banner-media')} style={media(S.images.banner)} {...mediaLabel(S.images.banner)} />
          <div className="lp-banner-scrim" />
          <div className="lp-banner-inner">
            <h1 className="lp-h1">
              {t('lp.banner.headline', S.banner.headline)}{' '}
              <em>{t('lp.banner.headlineEm', S.banner.headlineEm)}</em>
            </h1>
            <p className="lp-lede">{t('lp.banner.blurb', S.banner.blurb)}</p>
            <button type="button" className="lp-btn" onClick={request}>{t('hero.cta', S.banner.cta)}</button>
            <div className="lp-trust">
              <IconShield size={28} />
              <span className="lp-trust-t">{t('lp.banner.trust', S.banner.trustLine)}</span>
            </div>
          </div>
        </header>

        {/* ── The QPS Standard ────────────────────────────────────────── */}
        <section className="lp-sec" ref={refs.about}>
          <div className="lp-wrap">
            <h2 className="lp-eyebrow">{t('lp.standard.title', S.standard.title)}</h2>
            <div className="lp-pillars">
              {(t('lp.standard.pillars', S.standard.pillars) || []).map((p, i) => (
                <div key={i} className="lp-pillar">
                  <div className="lp-pillar-ico"><StandardIcon name={S.standard.pillars[i]?.icon} size={34} /></div>
                  <h3 className="lp-pillar-t">{p.title}</h3>
                  <p className="lp-pillar-b">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── What we do ──────────────────────────────────────────────── */}
        <section className="lp-sec" ref={refs.services} style={{ paddingTop: 0 }}>
          <div className="lp-wrap">
            <h2 className="lp-eyebrow">{t('lp.services.title', S.services.title)}</h2>
            <div className="lp-svcs">
              {(t('lp.services.items', S.services.items) || []).map((s, i) => (
                <div key={i} className="lp-svc">
                  <h3 className="lp-svc-t">{s.name}</h3>
                  <p className="lp-svc-b">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Featured projects ───────────────────────────────────────── */}
        <section className="lp-work" ref={refs.work}>
          <div className="lp-wrap"><h2 className="lp-eyebrow">{t('lp.work.title', S.work.title)}</h2></div>
          <div className="lp-strip">
            {projects.map((img, i) => (
              <div key={i} className={mediaClass(img, 'lp-tile')} style={media(img)} {...mediaLabel(img)}>
                {!img?.src && <div className="lp-tile-seal"><Seal size={64} /></div>}
              </div>
            ))}
          </div>
          <div className="lp-work-cta">
            <button type="button" className="lp-btn" onClick={request}>{t('lp.work.cta', S.work.cta)}</button>
          </div>
        </section>

        {/* ── Our process ─────────────────────────────────────────────── */}
        <section className="lp-sec lp-sec-dark" ref={refs.process}>
          <div className="lp-wrap">
            <h2 className="lp-eyebrow">{t('lp.process.title', S.process.title)}</h2>
            <div className="lp-steps">
              {(t('lp.process.steps', S.process.steps) || []).map((s, i) => (
                <div key={i} className="lp-step">
                  <div className="lp-step-n">{i + 1}</div>
                  <div className="lp-step-t">{s.label}</div>
                  <p className="lp-step-b">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Closing call to action ──────────────────────────────────── */}
        <section className="lp-close">
          <div className={mediaClass(S.images.closing, 'lp-close-media')} style={media(S.images.closing)} {...mediaLabel(S.images.closing)} />
          <div className="lp-close-scrim" />
          <div className="lp-close-inner">
            <h2 className="lp-close-h">
              {t('lp.closing.line1', S.closing.line1)}<br />
              {t('lp.closing.line2', S.closing.line2)}
            </h2>
            <button type="button" className="lp-close-cta" onClick={request}>
              {t('lp.closing.cta', S.closing.cta)}<span aria-hidden="true">→</span>
            </button>
          </div>
        </section>

        {/* ── Footer ──────────────────────────────────────────────────── */}
        <footer className="lp-foot">
          <div className="lp-foot-slogan">"{t('brand.slogan', BRAND.slogan)}"</div>
          <div style={{ marginTop: 8 }}>© {BRAND.copyrightYear} {BRAND.name.full} · {t('brand.location', BRAND.location)}</div>
          {/* Carriers reviewing our A2P text-messaging registration open these
              links by hand, so they have to be reachable from the public site. */}
          <div className="lp-foot-links">
            <button type="button" className="lp-foot-link" onClick={() => leaveFor('screen-privacy')}>Privacy Policy</button>
            ·
            <button type="button" className="lp-foot-link" onClick={() => leaveFor('screen-terms')}>Terms of Service</button>
          </div>
          <div className="lp-foot-dots" onClick={() => leaveFor('screen-admin-login')}>· · ·</div>
        </footer>
      </div>
    </div>
  );
}
